//
import type { AxiosCollection, AxiosErrors, AxiosItem } from '@/types/Components/Response'
import type { RegistrationData } from '@/types/Models/Account'
import type { Company, CompanyFilterType } from '@/types/Models/Company'
import type { CreateCustomer } from '@/types/Models/Customer'
import type { CustomersStoreState } from '@/types/Store'
import { defineStore } from 'pinia'
import { catchErrors, none } from '@/services/Helpers'
import { isSameObject } from '@/services/Utils'
import ApiProxy from '@/proxies/ApiProxy'
import CompanyProxy from '@/proxies/CompanyProxy'
import CompanyTransformer from '@/transformers/CompanyTransformer'

export const useCustomersStore = defineStore('customers', {
  state: (): CustomersStoreState => ({
    customer: null,
    allCustomers: [],
    listCustomers: [],
    filter: {} as CompanyFilterType,
    loading: false,
    count: 0,
  }),
  getters: {
    activeFilter: (state: CustomersStoreState) => state.filter?.active ?? 1,
    hasCustomPricesFilter: (state: CustomersStoreState) => state.filter?.hasCustomPrices ?? null,
  },
  actions: {
    patchCustomer({ id, data }: { id: string; data: Partial<Company> }) {
      this.listCustomers = this.listCustomers?.map((customer: Company) =>
        customer.id === id ? { ...customer, ...data } : customer,
      )
    },
    me() {
      return new CompanyProxy().me().then((response: AxiosItem<Company>) => {
        if (response?.data) {
          this.customer = response.data
        }
        return response
      })
    },
    create(payload: CreateCustomer) {
      return new CompanyProxy().create(payload)
    },
    archive(id: string) {
      return new CompanyProxy().updateField(id, 'isActive', 0)
    },
    unarchive(id: string) {
      return new CompanyProxy().updateField(id, 'isActive', 1)
    },
    async update(request: { id: string; data: Partial<Company> }) {
      const { id, data } = request
      this.patchCustomer(request)
      const payload = {
        ...data,
        ...(Object.prototype.hasOwnProperty.call(data, 'salesmanId') && data.salesmanId
          ? { assignedSalesman: (data as Company).salesmanId || null }
          : {}),
      }
      return new CompanyProxy().update(id, payload)
    },
    requestCreate(payload: RegistrationData) {
      return new ApiProxy().requestCreate(payload)
    },
    fetch(id: string) {
      return new CompanyProxy()
        .find(id)
        .then((response: AxiosItem<Company>) => {
          if (response?.data) {
            this.customer = response.data
          }
          return response
        })
        .catch(none)
    },
    all() {
      this.loading = true

      return new CompanyProxy(this.filter)
        .all()
        .then((response: AxiosCollection) => {
          this.listCustomers = CompanyTransformer.fetchCollection(response.data || [])
          this.count = response.count || 0
          return response
        })
        .catch((error: AxiosErrors) => {
          this.listCustomers = []
          this.count = 0
          catchErrors(error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    fetchAll(params: Record<string, string | number> = {}) {
      return new CompanyProxy(params).all().then((response: AxiosCollection) => {
        this.allCustomers = (response?.data || []).map((customer: Company) => ({
          ...customer,
          label:
            customer.name ||
            (customer.contactName && customer.contactName.length > 1
              ? `${customer.contactName} ${customer.contactLastName}`
              : customer.email),
          name:
            customer.name ||
            (customer.contactName ? `${customer.contactName} ${customer.contactLastName}` : customer.email),
          handle: customer.id,
          status: 'active',
        }))
        return response
      })
    },
    async setFilterAndReload(filter: Partial<CompanyFilterType> | null) {
      if (isSameObject(filter, this.filter)) return
      this.filter = filter || {}
      await this.all()
    },
    async updateFilterValue({ key, value }: { key: string; value: string | number | null }) {
      const filter = { ...this.filter, [key]: value }
      if (isSameObject(filter, this.filter)) return
      this.filter = filter
      await this.all()
    },
    async refresh() {
      await this.all()
    },
  },
})

export type CustomersStore = ReturnType<typeof useCustomersStore>
